import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import { ROUTES } from '../../constants/routes';
import { userHasSubApp, userHasFn, TILE_SUB_APP, SUB_APPS } from '../../utils/rbac';

const gridVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.055, delayChildren: 0.04 },
  },
};

const tileVariants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.22, ease: [0.25, 0.1, 0.25, 1] } },
};

const apps = [
  {
    id: 'service-requests',
    title: 'Service Requests',
    description: 'Submit and track maintenance and service requests across properties.',
    path: ROUTES.SERVICE_REQUESTS,
    available: true,
    buttonClass: 'btn-outline-primary',
  },
  {
    id: 'raise-the-sail',
    title: 'Raise The Sail Checklist',
    description: 'Checklist workflows (coming next in this program).',
    path: ROUTES.RAISE_THE_SAIL,
    available: true,
    buttonClass: 'btn-outline-primary',
  },
  {
    id: 'homesite-inspections',
    title: 'Homesite Inspections',
    description: 'Homesite inspection workflows.',
    path: ROUTES.HOMESITE,
    available: true,
    buttonClass: 'btn-outline-primary',
  },
  {
    id: 'risk-checklist',
    title: 'Risk Checklist',
    description: 'Risk assessment checklists by property.',
    path: ROUTES.RISK,
    available: true,
    buttonClass: 'btn-outline-primary',
  },
  {
    id: 'rules-violation',
    title: 'Rules Violation',
    description: 'Record and follow up on community rules violations.',
    path: ROUTES.RULES_VIOLATION,
    available: true,
    buttonClass: 'btn-outline-primary',
  },
  {
    id: 'hurricane',
    title: 'Hurricane',
    description: 'Storm damage inspections and reporting by site.',
    path: ROUTES.HURRICANE,
    available: true,
    buttonClass: 'btn-outline-primary',
  },
  {
    id: 'inventory',
    title: 'Inventory',
    description: 'Track property stock, storage locations, and installed items.',
    path: ROUTES.INVENTORY,
    available: true,
    buttonClass: 'btn-outline-primary',
  },
  {
    id: 'help',
    title: 'Help',
    description: 'Support contact, application version, and version documentation.',
    path: ROUTES.HELP,
    available: true,
    buttonClass: 'btn-outline-primary',
  },
];

function HomePage() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const filteredApps = apps.filter((app) => {
    if (!app.available || !app.path) return true;
    const subKey = TILE_SUB_APP[app.id];
    if (!subKey) return true;
    return userHasSubApp(user, subKey);
  });
  const visibleApps = userHasFn(user, SUB_APPS.HOME_ADMIN, 'panel')
    ? [
      ...filteredApps,
      {
        id: 'home-admin',
        title: 'Home Admin Panel',
        description: 'Manage users, properties, roles, and Microsoft SSO login settings.',
        path: ROUTES.HOME_ADMIN,
        available: true,
        buttonClass: 'btn-warning',
      },
    ]
    : filteredApps;

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN);
  };

  return (
    <div className="container mt-4 app-shell-page">
      <div className="app-page-header d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <h1 className="h3 mb-1">Compass App</h1>
          <p className="text-muted mb-0 small">Choose an application</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">
            {user?.name} ({user?.role_key || user?.role || '—'})
          </span>
          <button type="button" className="btn btn-outline-danger btn-sm" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {reduceMotion ? (
        <div className="row g-4">
          {visibleApps.map((app) => (
            <div key={app.id} className="col-md-6 col-lg-4">
              <div
                className={`card app-card h-100 ${app.available && app.path ? 'app-card-interactive' : ''}`}
              >
                <div className="card-body d-flex flex-column">
                  <h2 className="h5 card-title">{app.title}</h2>
                  <p className="card-text text-muted small flex-grow-1">{app.description}</p>
                  {app.available && app.path ? (
                    <button
                      type="button"
                      className={`btn ${app.buttonClass} mt-2`}
                      data-testid={`tile-open-${app.id}`}
                      onClick={() => navigate(app.path)}
                    >
                      Open
                    </button>
                  ) : (
                    <button type="button" className="btn btn-secondary mt-2" disabled>
                      Coming soon
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <motion.div
          className="row g-4"
          variants={gridVariants}
          initial="hidden"
          animate="show"
        >
          {visibleApps.map((app) => (
            <motion.div key={app.id} className="col-md-6 col-lg-4" variants={tileVariants}>
              <div
                className={`card app-card h-100 ${app.available && app.path ? 'app-card-interactive' : ''}`}
              >
                <div className="card-body d-flex flex-column">
                  <h2 className="h5 card-title">{app.title}</h2>
                  <p className="card-text text-muted small flex-grow-1">{app.description}</p>
                  {app.available && app.path ? (
                    <button
                      type="button"
                      className={`btn ${app.buttonClass} mt-2`}
                      data-testid={`tile-open-${app.id}`}
                      onClick={() => navigate(app.path)}
                    >
                      Open
                    </button>
                  ) : (
                    <button type="button" className="btn btn-secondary mt-2" disabled>
                      Coming soon
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
}

export default HomePage;
